/**
 * One Umami pageview per in-page tab move.
 *
 * The tabs of the hotel / prefecture / city pages (Bootstrap `.remote-tab`, assets/javascripts/utils/remote_tab.coffee)
 * and the Vue `shared/tabs` component keep the open tab in the URL hash — `/hotels/123#hotel-competitor` — by
 * assigning `location.hash`. The Umami tracker (m.js) sends a pageview on load and whenever `history.pushState` /
 * `replaceState` is called with a new URL, but it does not listen to `hashchange`, so a tab move left no trace
 * (2026-09-20: 8 tab clicks on staging, 0 collect requests). Umami keeps the fragment: the server stores
 * `/hotels/123#hotel-competitor` in `website_event.url_path` and a query string separately in `url_query`.
 *
 * On every `hashchange` this re-asserts the current URL with `history.replaceState`, which runs the tracker's hook:
 * it sends a pageview whose `url` is the new location (hash included) and whose `referrer` is the previous one.
 * Replacing the entry with the same URL adds nothing to the history, so back / forward keep working — and they
 * are counted the same way, because a traversal between two hashes fires `hashchange` too. An empty hash is
 * ignored: neither tab implementation switches tabs for it (they keep the current tab), so it is not a move.
 * Without the tracker `replaceState` is the plain browser call and nothing is sent.
 */
const INSTALLED = '__hpUmamiHashPageviews';

export function tabUrl(location) {
  const hash = (location.hash || '').replace(/^#/, '');
  if (!hash) return null;
  return `${location.pathname}${location.search}#${hash}`;
}

export function trackHashPageview(win = window) {
  const url = tabUrl(win.location);
  if (!url) return null;
  try {
    win.history.replaceState(win.history.state, '', url);
  } catch (e) {
    // Safari throttles history writes (SecurityError); analytics must never break the page
    return null;
  }
  return url;
}

/**
 * Listen once per page: every pack bundles shared_config, so this may run several times on one document.
 * Returns true when the listener was added by this call.
 */
export function installUmamiHashPageviews(win = window) {
  if (win[INSTALLED]) return false;
  win[INSTALLED] = true; // eslint-disable-line no-param-reassign
  win.addEventListener('hashchange', () => trackHashPageview(win));
  return true;
}
